import { ApiService } from "./api";
import { RemoteLogger } from "./remoteLogger";

export interface Contact {
  id: number;
  name: string;
  role: string | number;
  roleId?: number;
  email?: string;
  avatar?: string;
  memberships?: any[];
  groups?: any[];
  groupIds?: number[];
}

export function resolveRole(u: any): string {
  if (!u) return "Member";
  const userObj = u.user || u;

  // 1. Try to find a jobTitle from memberships
  const membershipsList = u.memberships || userObj.memberships || [];
  if (Array.isArray(membershipsList)) {
    const foundMem = membershipsList.find(
      (m: any) => m && typeof m.jobTitle === "string" && m.jobTitle.trim() !== ""
    );
    if (foundMem) {
      return foundMem.jobTitle.trim();
    }
  }

  // 2. Map role numbers to human-readable strings
  const rawRole = userObj.role;
  if (
    typeof rawRole === "number" ||
    (typeof rawRole === "string" && rawRole.trim() !== "" && !isNaN(Number(rawRole)))
  ) {
    const rNum = Number(rawRole);
    switch (rNum) {
      case 1:
        return "Member";
      case 2:
        return "Approver";
      case 3:
        return "Manager";
      case 4:
        return "Admin";
      default:
        return ".";
    }
  }

  // 3. Fallback to title, designation, or string role
  return userObj.title || userObj.designation || (typeof rawRole === "string" ? rawRole : "Member");
}

export const fallbackContacts: Contact[] = [
  { id: 1, name: "Admin User", role: "Administrator", email: "admin@truapp.com" },
  { id: 2, name: "Support Agent", role: "Support", email: "support@truapp.com" },
];

export class UserService {
  private static ENDPOINT = "/users";

  static async listAll(
    token: string
  ): Promise<{ data?: Contact[]; status: number; error?: string }> {
    try {
      const response = await ApiService.get<any>(`${this.ENDPOINT}?perPage=100`, token);

      if (response.data) {
        // Handle various response layouts from different backend versions
        const rawUsers =
          (response.data as any).data?.results ||
          (response.data as any).data ||
          (response.data as any).results ||
          (Array.isArray(response.data) ? response.data : []);

        const mappedUsers = rawUsers.map((u: any) => ({
          id: u.id,
          name: u.firstName
            ? `${u.firstName} ${u.lastName || ""}`.trim()
            : u.username || u.email || "Unknown User",
          role: resolveRole(u),
          roleId: typeof u.role === "number" ? u.role : undefined,
          email: u.email,
          avatar: u.avatar,
          memberships: u.memberships || [],
          groups: u.groups || [],
          groupIds: u.groupIds || [],
        }));

        return { data: mappedUsers, status: response.status };
      }
      return { data: [], status: response.status, error: response.error };
    } catch (err: any) {
      return { data: [], status: 500, error: err?.message || "Unknown error" };
    }
  }

  static async getMemberships(
    token: string
  ): Promise<{ success: boolean; data?: any[]; message?: string }> {
    try {
      const response = await ApiService.get<any>("/mobile/memberships/me", token);
      if (response.data) {
        return response.data;
      }
      return { success: false, data: [] };
    } catch (err: any) {
      return { success: false, data: [] };
    }
  }

  static async listFromNetwork(
    token: string,
    templateId: number,
    orgId?: number
  ): Promise<{ data?: Contact[]; status: number; error?: string }> {
    try {
      const orgParam = orgId !== undefined ? `&orgId=${orgId}` : "";
      const endpoint = `/network?templateId=${templateId}${orgParam}&roleIds=1&roleIds=2&roleIds=3&roleIds=4&context=template&perPage=100`;
      const response = await ApiService.get<any>(endpoint, token);
      RemoteLogger.info("rauw userse", response.data);

      if (response.data) {
        const rawUsers =
          (response.data as any).data?.results ||
          (response.data as any).data ||
          (response.data as any).results ||
          (Array.isArray(response.data) ? response.data : []);

        RemoteLogger.info(rawUsers, "rauw userse");

        const mappedUsers = rawUsers.map((u: any) => {
          const userObj = u.user || u;

          return {
            id: userObj.id,
            name:
              `${userObj.contact.firstName} ${userObj.contact.lastName}`.trim() || "Unknown User",
            role: resolveRole(u),
            roleId: typeof userObj.role === "number" ? userObj.role : undefined,
            email: userObj.contact.email,
            avatar: userObj.media?.[0]?.publicUrl || userObj.media?.[0]?.filePath || userObj.avatar,
            memberships: u.memberships || userObj.memberships || [],
            groups: u.groups || userObj.groups || [],
            groupIds: u.groupIds || userObj.groupIds || [],
          };
        });

        return { data: mappedUsers, status: response.status };
      }
      return { data: [], status: response.status, error: response.error };
    } catch (err: any) {
      RemoteLogger.error(err.message);
      return { data: [], status: 500, error: err?.message || "Unknown error" };
    }
  }

  static async analyzeRequiredApprovers(
    token: string,
    templateId: number,
    values: { inputFieldId: number; value: string }[]
  ): Promise<{ data?: any; status: number; error?: string }> {
    try {
      const endpoint = "/network/analyze-required-approvers";
      const payload = {
        templateId,
        values,
      };
      const response = await ApiService.post<any>(endpoint, payload, token);
      return { data: response.data, status: response.status, error: response.error };
    } catch (err: any) {
      return { status: 500, error: err?.message || "Unknown error" };
    }
  }
}
